"use client";

import { Button, cn } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  BarChart3,
  CalendarDays,
  RefreshCw,
  UserRound,
} from "lucide-react";
import type { ShiftWeekStats } from "@/modules/merchant/presentation/lib/shift-assignment-status";

export type ShopShiftListToolbarProps = {
  shopId: string | undefined;
  branchName: string | null;
  weekLabel: string;
  stats: ShiftWeekStats;
  isRefreshing?: boolean;
  onRefresh?: () => void;
};

export function ShopShiftListToolbar({
  shopId,
  branchName,
  weekLabel,
  stats,
  isRefreshing,
  onRefresh,
}: ShopShiftListToolbarProps) {
  return (
    <header className="mb-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="border-border border-b bg-muted/25 px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-bold font-heading text-foreground text-xl uppercase tracking-tight sm:text-2xl">
                ລາຍການກະ
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 font-semibold text-[10px] text-violet-900 uppercase tracking-wide dark:text-violet-200">
                <CalendarDays className="size-3" aria-hidden />
                ກະ POS
              </span>
              {stats.total > 0 ? (
                <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground text-xs tabular-nums">
                  {stats.total} ກະ
                </span>
              ) : null}
              {stats.open > 0 ? (
                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-medium text-emerald-800 text-xs tabular-nums dark:text-emerald-200">
                  {stats.open} ເປີດ
                </span>
              ) : null}
            </div>

            <p className="mt-1.5 text-muted-foreground text-sm leading-snug">
              ເບິ່ງປະຫວັດກະທີ່ເປີດຈາກ POS — ຈັດຕາມວັນ ແລະ ພະນັກງານ
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              {branchName ? (
                <span className="font-medium text-foreground text-sm">
                  {branchName}
                </span>
              ) : null}
              {branchName ? (
                <span className="text-muted-foreground/50">·</span>
              ) : null}
              <span className="text-muted-foreground tabular-nums">
                {weekLabel}
              </span>
            </div>
          </div>

          {onRefresh ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-1.5 border-border px-3"
              disabled={isRefreshing}
              onClick={onRefresh}
              title="ໂຫຼດຄືນ"
            >
              <RefreshCw
                className={cn("size-4", isRefreshing && "animate-spin")}
                aria-hidden
              />
              <span className="hidden sm:inline">ໂຫຼດຄືນ</span>
            </Button>
          ) : null}
        </div>
      </div>

      {shopId ? (
        <div className="px-4 py-3 sm:px-5">
          <p className="mb-2 font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
            ວຽກດ່ວນ
          </p>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:thin]">
            <Link
              to="/app/shops/$shopId/shifts/me"
              params={{ shopId }}
              className={cn(
                "inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 font-medium text-sm transition-colors touch-manipulation",
                "border-border bg-muted/30 hover:bg-muted/50",
              )}
            >
              <UserRound className="size-4 shrink-0" aria-hidden />
              <span>ກະຂອງຂ້ອຍ</span>
            </Link>
            <Link
              to="/app/shops/$shopId/shifts/reports"
              params={{ shopId }}
              className={cn(
                "inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 font-medium text-sm transition-colors touch-manipulation",
                "border-border bg-muted/30 hover:bg-muted/50",
              )}
            >
              <BarChart3 className="size-4 shrink-0" aria-hidden />
              <span>ລາຍງານ</span>
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}
